import { useEffect, useRef } from 'react';
import api from '../../../api';

/**
 * Generic realtime hook around api.subscribe.
 * Handlers are optional. Re-subscribes when `deps` change.
 *
 * Options:
 *   - debounce: number (ms) - debounce handler calls
 *
 * Usage:
 *   useRealtimeUpdates({
 *     onAny: (u) => {},
 *     onUser: (u) => {},
 *     onPassword: (p) => {},
 *     onPort: (p) => {},
 *   }, [deps], { debounce: 3000 });
 */
export default function useRealtimeUpdates(handlers = {}, deps = [], options = {}) {
  const handlersRef = useRef(handlers);
  const debounceTimeoutsRef = useRef({});
  const { debounce = 0 } = options;

  handlersRef.current = handlers;

  useEffect(() => {
    if (!api || typeof api.subscribe !== 'function') {
      return () => {};
    }

    const callHandler = (handlerName, data) => {
      if (typeof handlersRef.current[handlerName] !== 'function') {
        return;
      }

      if (debounce > 0) {
        // Debounced call
        if (debounceTimeoutsRef.current[handlerName]) {
          clearTimeout(debounceTimeoutsRef.current[handlerName]);
        }
        debounceTimeoutsRef.current[handlerName] = setTimeout(() => {
          handlersRef.current[handlerName](data);
          delete debounceTimeoutsRef.current[handlerName];
        }, debounce);
      } else {
        // Immediate call
        handlersRef.current[handlerName](data);
      }
    };

    const unsubscribe = api.subscribe((update) => {
      try {
        // optional catch-all
        callHandler('onAny', update);

        if (!update || !update.type) return;

        if (update.type === 'user_update') {
          callHandler('onUser', update.data);
        } else if (update.type === 'password_update') {
          callHandler('onPassword', update.data);
        } else if (update.type === 'port_update') {
          callHandler('onPort', update.data);
        } else if (update.type === 'activation_update') {
          callHandler('onActivation', update.data);
        } else if (update.type === 'port_activity_update') {
          callHandler('onActivity', update.data);
        }
      } catch (err) {
        console.error('Error in realtime update handler:', err);
      }
    });

    return () => {
      // Clear any pending debounce timeouts
      Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
      debounceTimeoutsRef.current = {};
      
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
